import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraInput, SpecraOverlayDialog, SpecraSelect } from '@mono/ui-components';
import { SpecraCheckboxField, SpecraFormField, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { categoriesFor, SENSITIVITIES } from './categories';
import { documentErrorText, useLibraryReferences, useSelectableReleases } from './useProposalDocuments';
const T = Testids.proposalDocuments;
function footerFor(p) {
    return {
        confirmLabel: p.label,
        cancelLabel: 'Cancel',
        onConfirm: p.onConfirm,
        onCancel: p.onCancel,
        loading: p.busy,
        loadingLabel: 'Working…',
        confirmDisabled: p.busy || p.disabled,
        cancelProps: {
            disabled: p.busy
        },
        confirmProps: {
            'data-testid': T.confirm
        }
    };
}
function ErrorBanner({ error }) {
    if (!error) return null;
    return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "danger",
        role: "alert",
        "data-testid": T.actionError,
        children: documentErrorText(error)
    });
}
export function UploadDialog(p) {
    const categories = categoriesFor(p.canUpload);
    const [file, setFile] = useState(null);
    const [category, setCategory] = useState(categories[0]?.value ?? '');
    const [sensitivity, setSensitivity] = useState('');
    const m = p.writes.upload;
    const submit = ()=>{
        if (!file || m.isPending) return;
        m.mutate({
            file,
            category,
            sensitivity
        }, {
            onSuccess: p.onClose
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (o)=>!o && !m.isPending && p.onClose(),
        title: "Upload a document",
        description: "Choose the file, then say what kind of document it is.",
        testId: T.uploadDialog,
        footerProps: footerFor({
            label: 'Upload',
            busy: m.isPending,
            disabled: !file,
            onConfirm: submit,
            onCancel: p.onClose
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(ErrorBanner, {
                    error: m.error
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "File",
                    htmlFor: "pd-upload-file",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "pd-upload-file",
                        type: "file",
                        "data-testid": T.uploadFile,
                        onChange: (e)=>setFile(e.target.files?.[0] ?? null)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Category",
                    htmlFor: "pd-upload-category",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-upload-category",
                        "data-testid": T.categorySelect,
                        value: category,
                        onChange: (e)=>setCategory(e.target.value),
                        children: categories.map((c)=>/*#__PURE__*/ _jsx("option", {
                                value: c.value,
                                children: c.label
                            }, c.value))
                    })
                }),
                p.canUpload ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Who can see it",
                    htmlFor: "pd-upload-sensitivity",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-upload-sensitivity",
                        "data-testid": T.sensitivitySelect,
                        value: sensitivity,
                        onChange: (e)=>setSensitivity(e.target.value),
                        children: SENSITIVITIES.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.value,
                                children: s.label
                            }, s.value))
                    })
                }) : null
            ]
        })
    });
}
export function EditDialog(p) {
    const categories = categoriesFor(p.canUpload);
    const [category, setCategory] = useState(p.doc.category);
    const [sensitivity, setSensitivity] = useState(p.doc.sensitivity || 'standard');
    const m = p.writes.update;
    const submit = ()=>{
        if (m.isPending) return;
        // Only send what changed.
        m.mutate({
            documentId: p.doc.id,
            category: category !== p.doc.category ? category : undefined,
            sensitivity: sensitivity !== (p.doc.sensitivity || 'standard') ? sensitivity : undefined
        }, {
            onSuccess: p.onClose
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (o)=>!o && !m.isPending && p.onClose(),
        title: "Change details",
        description: p.doc.title,
        testId: T.editDialog,
        footerProps: footerFor({
            label: 'Save',
            busy: m.isPending,
            onConfirm: submit,
            onCancel: p.onClose
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(ErrorBanner, {
                    error: m.error
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Category",
                    htmlFor: "pd-edit-category",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        id: "pd-edit-category",
                        "data-testid": T.categorySelect,
                        value: category,
                        onChange: (e)=>setCategory(e.target.value),
                        children: [
                            !categories.some((c)=>c.value === p.doc.category) ? /*#__PURE__*/ _jsx("option", {
                                value: p.doc.category,
                                children: p.doc.category
                            }) : null,
                            categories.map((c)=>/*#__PURE__*/ _jsx("option", {
                                    value: c.value,
                                    children: c.label
                                }, c.value))
                        ]
                    })
                }),
                p.canUpload ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Who can see it",
                    htmlFor: "pd-edit-sensitivity",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-edit-sensitivity",
                        "data-testid": T.sensitivitySelect,
                        value: sensitivity,
                        onChange: (e)=>setSensitivity(e.target.value),
                        children: SENSITIVITIES.filter((s)=>s.value !== '').map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.value,
                                children: s.label
                            }, s.value))
                    })
                }) : null
            ]
        })
    });
}
export function RemoveDialog(p) {
    const m = p.writes.remove;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (o)=>!o && !m.isPending && p.onClose(),
        title: "Remove this document?",
        description: "It leaves this proposal's documents. You can add it again later by uploading it or bringing it in.",
        size: "sm",
        testId: T.removeDialog,
        footerProps: footerFor({
            label: 'Remove',
            busy: m.isPending,
            onConfirm: ()=>!m.isPending && m.mutate(p.doc.id, {
                    onSuccess: p.onClose
                }),
            onCancel: p.onClose
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm",
                    children: p.doc.title
                }),
                /*#__PURE__*/ _jsx(ErrorBanner, {
                    error: m.error
                })
            ]
        })
    });
}
/** Pursuit documents not yet in the proposal; the caller picks which to bring in. */ export function BringInDialog(p) {
    const [picked, setPicked] = useState(p.candidates.length === 1 ? [
        p.candidates[0].artifactId
    ] : []);
    const [category, setCategory] = useState('other');
    const m = p.writes.bringIn;
    const toggle = (id)=>setPicked((cur)=>cur.includes(id) ? cur.filter((x)=>x !== id) : [
                ...cur,
                id
            ]);
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (o)=>!o && !m.isPending && p.onClose(),
        title: "Bring in from the pursuit",
        description: "Choose the pursuit documents to add to this proposal.",
        testId: T.bringInDialog,
        footerProps: footerFor({
            label: 'Bring in',
            busy: m.isPending,
            disabled: picked.length === 0,
            onConfirm: ()=>!m.isPending && m.mutate({
                    artifactIds: picked,
                    category
                }, {
                    onSuccess: p.onClose
                }),
            onCancel: p.onClose
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(ErrorBanner, {
                    error: m.error
                }),
                p.candidates.map((d)=>/*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                            label: d.title,
                            description: d.filename,
                            controlProps: {
                                checked: picked.includes(d.artifactId),
                                onCheckedChange: ()=>toggle(d.artifactId)
                            }
                        })
                    }, d.id)),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "File them as",
                    htmlFor: "pd-bring-category",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-bring-category",
                        "data-testid": T.categorySelect,
                        value: category,
                        onChange: (e)=>setCategory(e.target.value),
                        children: categoriesFor(true).map((c)=>/*#__PURE__*/ _jsx("option", {
                                value: c.value,
                                children: c.label
                            }, c.value))
                    })
                })
            ]
        })
    });
}
export function LibraryDialog(p) {
    const releases = useSelectableReleases(true);
    const [releaseId, setReleaseId] = useState('');
    const [picked, setPicked] = useState([]);
    const [category, setCategory] = useState('reference');
    const release = releases.data?.find((r)=>r.releaseId === releaseId);
    const refs = useLibraryReferences(release?.libraryItemId ?? '');
    const m = p.writes.libraryImport;
    const toggle = (id)=>setPicked((cur)=>cur.includes(id) ? cur.filter((x)=>x !== id) : [
                ...cur,
                id
            ]);
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (o)=>!o && !m.isPending && p.onClose(),
        title: "Import from the Library",
        description: "Pick a released Library item and the files you want in this proposal.",
        testId: T.libraryDialog,
        footerProps: footerFor({
            label: 'Import',
            busy: m.isPending,
            disabled: !release || picked.length === 0,
            onConfirm: ()=>release && !m.isPending && m.mutate({
                    releaseId: release.releaseId,
                    artifactIds: picked,
                    category
                }, {
                    onSuccess: p.onClose
                }),
            onCancel: p.onClose
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(ErrorBanner, {
                    error: m.error
                }),
                releases.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    role: "alert",
                    children: "The Library could not be loaded. Close this and try again."
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Library item",
                    htmlFor: "pd-library-release",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        id: "pd-library-release",
                        "data-testid": T.libraryRelease,
                        value: releaseId,
                        onChange: (e)=>{
                            setReleaseId(e.target.value);
                            setPicked([]);
                        },
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: releases.isPending ? 'Loading…' : 'Choose an item'
                            }),
                            (releases.data ?? []).map((r)=>/*#__PURE__*/ _jsxs("option", {
                                    value: r.releaseId,
                                    children: [
                                        r.releasedTitle,
                                        " (v",
                                        r.version,
                                        ")"
                                    ]
                                }, r.releaseId))
                        ]
                    })
                }),
                release && refs.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    role: "alert",
                    children: "This item's files could not be loaded."
                }) : null,
                release && refs.data ? /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        refs.data.direct.length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-sm",
                            children: "This item has no files that can be imported."
                        }) : refs.data.direct.map((r)=>/*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                label: r.label || r.artifactId,
                                controlProps: {
                                    checked: picked.includes(r.artifactId),
                                    onCheckedChange: ()=>toggle(r.artifactId)
                                }
                            }, r.id)),
                        refs.data.hidden > 0 ? /*#__PURE__*/ _jsxs("p", {
                            className: "text-xs",
                            children: [
                                refs.data.hidden,
                                " other file(s) are pinned versions or archives and cannot be imported here."
                            ]
                        }) : null
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "File them as",
                    htmlFor: "pd-library-category",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-library-category",
                        "data-testid": T.categorySelect,
                        value: category,
                        onChange: (e)=>setCategory(e.target.value),
                        children: categoriesFor(true).map((c)=>/*#__PURE__*/ _jsx("option", {
                                value: c.value,
                                children: c.label
                            }, c.value))
                    })
                })
            ]
        })
    });
}
